import { DataRoomInviteRefusal } from 'shared';

import { StringKey } from '../../lang/index';

type RefusalCopy = { body?: StringKey; title: StringKey };

/**
 * One state for every link that cannot be used, whichever thing retired it.
 *
 * Revoked, superseded and expired were three sentences, and a person holding a dead link can act
 * on none of the differences between them: the one thing left to do is go back to the inbox.
 * Saying which room was withdrawn, or that an invitation once existed, also tells somebody being
 * turned away more than they are owed.
 *
 * The exception is the address, not the link. A signed-in account that is not the one invited can
 * be changed by the person reading the page, and their link still works — so that case keeps a
 * sentence of its own.
 *
 * The map stays exhaustive over the shared enum: a code added on the server has to be placed in
 * one of the two groups here rather than quietly inheriting either.
 */
const LINK_RETIRED: RefusalCopy = { title: 'INVITE_NO_LONGER_AVAILABLE_TITLE' };

const REFUSAL_COPY: Record<DataRoomInviteRefusal, RefusalCopy> = {
  [DataRoomInviteRefusal.EMAIL_MISMATCH]: {
    body: 'INVITE_EMAIL_MISMATCH_BODY',
    title: 'INVITE_EMAIL_MISMATCH_TITLE',
  },
  [DataRoomInviteRefusal.EXPIRED]: LINK_RETIRED,
  [DataRoomInviteRefusal.REVOKED]: LINK_RETIRED,
  [DataRoomInviteRefusal.UNKNOWN]: LINK_RETIRED,
};

const isRefusal = (code: unknown): code is DataRoomInviteRefusal =>
  typeof code === 'string' && (Object.values(DataRoomInviteRefusal) as string[]).includes(code);

/** Anything the server did not label is a link that did not work, which is all the page claims. */
export const refusalCopyOf = (code: unknown): RefusalCopy =>
  REFUSAL_COPY[isRefusal(code) ? code : DataRoomInviteRefusal.UNKNOWN];
